import {
  channelRoute,
  formatRoute,
  isChannelId,
  pickImage,
  readPastedAddress,
  type ChannelDetails,
  type ChannelSummary,
  type FeedItem,
  type ImageRef,
  type Route,
  type SubscriptionRow,
} from '@visious/core';
import { describeAuthProblem, type ProblemCopy, type ProblemSubject } from '../../api/problem';
import type { AuthApiProblem } from '../../api/auth';
import { fmt, text } from '../../strings';
import type { MenuModel } from '../../ui/menuModel';

/**
 * The manage-subscriptions page (`/feed/channels`) as reasoning — R13: no DOM, no React.
 *
 * Two halves. The **list** is the strip the Subscriptions feed used to draw, grown into the page it
 * always wanted to be: names first, avatars hydrated lazily (they cost ~50 KB each), each tile
 * carrying its own two-click Unsubscribe — the selection is retired. The **add card** takes
 * anything a reader might paste —
 * a channel URL, an `@handle`, a bare UCID — and falls back to a channel-name search for plain text,
 * producing candidate rows whose Subscribe button is the same control every channel page has.
 */

/* ------------------------------------------------------------------------------------------------
 * The add card — what one line of input means
 * ---------------------------------------------------------------------------------------------- */

export type AddQuery =
  | { readonly kind: 'empty' }
  /** A UCID, either bare or read out of a pasted URL — no request needed to know what it names. */
  | { readonly kind: 'channel'; readonly channelId: string }
  /** `@handle`, `/c/NAME`, `/user/NAME` — one `resolveurl` call away from a UCID. */
  | { readonly kind: 'name'; readonly route: Route }
  /** Plain text: a channel-name search. */
  | { readonly kind: 'search'; readonly q: string };

/**
 * `readPastedAddress` does the reading — the URL scheme ignores origins on purpose, so a full
 * `youtube.com` URL, an instance URL and a bare `/@handle` all land in the same arms the router uses.
 * What does not read as a channel is a search, never an error: the reader typed *something*, and "no
 * results" is a better answer than "unrecognised input".
 *
 * **It is `readPastedAddress` rather than `parseRoute` for the reason the header's box is** (2026-09-06):
 * since a single unmatched segment reads as a vanity channel name, `parseRoute('lofi')` is a name to
 * look up, and this box would have answered a typed word with one failed lookup instead of a list of
 * channels. That gate asks whether the text is *shaped like an address* at all — a scheme, a leading
 * host, a path or a handle — so a pasted `youtube.com/vsauce` still resolves and a typed `vsauce`
 * still searches.
 */
export function classifyAddInput(raw: string): AddQuery {
  const trimmed = raw.trim();
  if (trimmed === '') return { kind: 'empty' };
  if (isChannelId(trimmed)) return { kind: 'channel', channelId: trimmed };

  const route = readPastedAddress(trimmed);
  if (route?.kind === 'channel' && isChannelId(route.channelId)) {
    return { kind: 'channel', channelId: route.channelId };
  }
  if (route?.kind === 'channelName') return { kind: 'name', route };

  return { kind: 'search', q: trimmed };
}

/** A channel the add card found — a search hit or a resolved link — ready for a Subscribe button. */
export interface CandidateRow {
  readonly channelId: string;
  readonly href: string;
  readonly name: string;
  readonly verified: boolean;
  /** YouTube's "- Topic" auto-channels, marked so subscribing to one is a choice rather than a slip. */
  readonly autoGenerated: boolean;
  readonly avatarUrl: string | null;
  readonly subscriberText: string | null;
}

/** The smallest avatar still ≥ 40 px — the row is drawn at 40, and `pickImage` says why not larger. */
function avatarOf(images: readonly ImageRef[]): string | null {
  return pickImage(images, { minWidth: 40 })?.url ?? null;
}

export function candidateFromSummary(channel: ChannelSummary): CandidateRow {
  return {
    channelId: channel.channelId,
    href: formatRoute(channelRoute(channel.channelId)),
    name: channel.name,
    verified: channel.verified,
    autoGenerated: channel.autoGenerated,
    avatarUrl: avatarOf(channel.thumbnails),
    subscriberText: channel.subscriberCount === null ? null : fmt().count(channel.subscriberCount),
  };
}

export function candidateFromDetails(channel: ChannelDetails): CandidateRow {
  return {
    channelId: channel.channelId,
    href: formatRoute(channelRoute(channel.channelId)),
    name: channel.name,
    verified: channel.verified,
    autoGenerated: channel.autoGenerated,
    avatarUrl: avatarOf(channel.avatars),
    subscriberText: channel.subscriberCount === null ? null : fmt().count(channel.subscriberCount),
  };
}

/** The channel rows out of a mixed search answer. Videos and playlists in a `type=channel` search
 *  would be a bug of the instance's; whatever arrives, only channels are candidates. */
export function candidatesFromSearch(items: readonly FeedItem[]): readonly CandidateRow[] {
  return items.flatMap((item) => (item.kind === 'channel' ? [candidateFromSummary(item.channel)] : []));
}

/* ------------------------------------------------------------------------------------------------
 * The subscription list
 * ---------------------------------------------------------------------------------------------- */

/** Where the header's Done goes: back to the feed this page is the sub-page of. */
export const MANAGE_DONE_HREF = formatRoute({ kind: 'feed', feed: 'subscriptions' });

/** One subscription, ready to render. `avatarUrl` is resolved against the *current* instance (R14). */
export interface ManageRow {
  readonly channelId: string;
  readonly href: string;
  readonly name: string;
  /** The letters drawn while the avatar has not been fetched — never a spinner where a name belongs. */
  readonly initials: string;
  readonly avatarUrl: string | null;
  readonly subscriberText: string | null;
  /** Deleted or terminated on YouTube (`isChannelGone`) — the tile says so instead of a count. */
  readonly gone: boolean;
}

/**
 * The sort is display-only like the filter beside it. `default` is the instance's own order —
 * the one honest "as stored" reading — and the two name sorts are everything the list can offer
 * up front: subscriber counts hydrate lazily (~50 KB per channel), so sorting by them would
 * reorder the grid as responses land.
 */
export type ManageSort = 'default' | 'name-asc' | 'name-desc';

/** The sort as a menu — built here so `view.tsx` renders props, the search screen's rule. */
export function manageSortMenu(sort: ManageSort): MenuModel {
  const words = text().subscriptions.manage.list.sort;
  const labels: Record<ManageSort, string> = {
    'default': words.listOrder,
    'name-asc': words.nameAsc,
    'name-desc': words.nameDesc,
  };
  return {
    label: words.label,
    value: labels[sort],
    valueActive: sort !== 'default',
    sections: [
      {
        title: null,
        items: (['default', 'name-asc', 'name-desc'] as const).map((id) => ({
          id,
          label: labels[id],
          detail: null,
          selected: sort === id,
        })),
      },
    ],
    note: null,
  };
}

export interface ManageListState {
  /** What the grid draws — the rows the filter lets through, in the chosen order. */
  readonly rows: readonly ManageRow[];
  /** Every subscription, filtered or not: the heading's number must not change while typing. */
  readonly count: number;
  /** The filter matched nothing — a sentence, because an empty grid reads as "no subscriptions". */
  readonly nothingMatches: boolean;
  readonly problem: ProblemCopy | null;
  readonly loading: boolean;
}

export function manageListState(input: {
  readonly rows: readonly SubscriptionRow[] | null;
  readonly problem: AuthApiProblem | null;
  readonly filter: string;
  readonly sort: ManageSort;
  readonly resolveAsset: (path: string) => string | null;
}): ManageListState {
  if (input.problem !== null) {
    return {
      rows: [],
      count: 0,
      nothingMatches: false,
      problem: describeAuthProblem(input.problem, listProblemSubject()),
      loading: false,
    };
  }
  if (input.rows === null) {
    return { rows: [], count: 0, nothingMatches: false, problem: null, loading: true };
  }

  const rows = input.rows.map((row) => ({
    channelId: row.channelId,
    href: formatRoute(channelRoute(row.channelId)),
    name: row.name,
    initials: initialsOf(row.name),
    avatarUrl: row.avatarPath === null ? null : input.resolveAsset(row.avatarPath),
    subscriberText: row.subscriberCount === null ? null : fmt().count(row.subscriberCount),
    gone: row.gone,
  }));

  // Case-folded substring, nothing cleverer: the list is the reader's own subscriptions, so they
  // are matching against names they know. An empty filter is the whole list.
  const needle = input.filter.trim().toLocaleLowerCase();
  const shown = needle === '' ? rows : rows.filter((row) => row.name.toLocaleLowerCase().includes(needle));

  // `localeCompare` rather than `<`: these are channel names, and code-point order files "Ötzi"
  // after "Z". The default sort leaves the instance's own order untouched.
  const sorted =
    input.sort === 'default'
      ? shown
      : [...shown].sort(
          (a, b) => a.name.localeCompare(b.name) * (input.sort === 'name-asc' ? 1 : -1),
        );

  return {
    rows: sorted,
    count: rows.length,
    nothingMatches: rows.length > 0 && shown.length === 0,
    problem: null,
    loading: false,
  };
}

/**
 * Up to two letters from the channel's own name. Code-point aware, because a name beginning with an
 * emoji or an astral character would otherwise be sliced into half a surrogate pair and render as a
 * replacement glyph — which the API supplies more often than one would hope.
 */
function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/u).filter(Boolean);
  const letters = words.slice(0, 2).map((word) => [...word][0] ?? '');
  return letters.join('').toUpperCase();
}

function listProblemSubject(): ProblemSubject {
  const words = text().subscriptions.problem;
  return {
    notFound: words.listNotFound,
    unreadable: () => words.listUnreadable,
  };
}
